/**
 * The form a blocked agent is waiting on.
 *
 * It sits between the transcript and the composer, taking the composer's place
 * in the eye's path, because while a question is pending there is nothing else
 * to do in this session: the turn is stopped until it is answered.
 *
 * The question is read from the session log, and the answer is written back to
 * the same log. Nothing here talks to the agent directly — that is the point.
 * The peer that asked may be a daemon on a laptop, and this may be a phone.
 *
 * Rendering is deliberately close to Claude Code's own AskUserQuestion so the
 * shape a model was trained to expect is the shape the person sees: a chip for
 * the header, options as cards with a description, an always-present "Other"
 * for free text, and previews revealed beneath the option they belong to.
 *
 * One question is shown at a time. The form shares a fixed-height column with
 * the transcript above it, so a four-question ask rendered all at once has no
 * room to be: it squeezes the transcript to nothing and still overflows. Paging
 * keeps the form roughly the size of one question no matter how many were
 * asked, and the option list scrolls within its own cap for the rare long one.
 */

import type {
  InteractionAnswerValue,
  InteractionQuestion,
  QuestionBody,
} from '@mod/core/model'
import { Check, ChevronLeft } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { StatusDot } from '../../shell/window/StatusDot'
import { Markdown } from './Markdown'

/** The sentinel value for the always-available free-text choice. */
export const OTHER = '__other__'

export type Selection = Record<string, { values: string[]; freeText: string }>

function initialSelection(body: QuestionBody): Selection {
  const out: Selection = {}
  for (const q of body.questions) out[q.id] = { values: [], freeText: '' }
  return out
}

/**
 * Every question needs an answer before the form can be submitted: a partial
 * reply would reach the model as a silently dropped question (`buildToolResult`
 * skips empty answers), which reads to it as "the user declined to say" rather
 * than "the user has not finished typing".
 */
export function isComplete(body: QuestionBody, selection: Selection): boolean {
  return body.questions.every((q) => isAnswered(selection[q.id]))
}

/**
 * Whether one question has an answer worth sending — the same rule `isComplete`
 * applies to all of them, and what gates paging forward to the next one.
 */
export function isAnswered(
  state: { values: string[]; freeText: string } | undefined,
): boolean {
  if (!state) return false
  if (state.values.includes(OTHER)) return state.freeText.trim().length > 0
  return state.values.length > 0
}

export function toAnswers(
  body: QuestionBody,
  selection: Selection,
): Record<string, InteractionAnswerValue> {
  const answers: Record<string, InteractionAnswerValue> = {}
  for (const q of body.questions) {
    const state = selection[q.id]
    if (!state) continue
    // OTHER is a UI affordance, not an option the model offered, so it never
    // reaches the answer — the typed text carries it instead.
    const selected = state.values.filter((v) => v !== OTHER)
    const freeText = state.freeText.trim()
    answers[q.id] = {
      selected,
      ...(freeText.length > 0 ? { freeText } : {}),
    }
  }
  return answers
}

function OptionCard({
  label,
  description,
  preview,
  checked,
  multi,
  onToggle,
}: {
  label: string
  description?: string
  preview?: string
  checked: boolean
  multi: boolean
  onToggle: () => void
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={checked}
        {...(description ? { title: description } : {})}
        className={`flex w-full items-start gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors ${
          checked
            ? 'border-blue-500 bg-blue-500/[0.06]'
            : 'border-border hover:bg-muted'
        }`}
      >
        <span
          className={`mt-px flex size-3.5 shrink-0 items-center justify-center border ${
            multi ? 'rounded-[4px]' : 'rounded-full'
          } ${
            checked
              ? 'border-blue-500 bg-blue-500 text-white'
              : 'border-foreground/25'
          }`}
        >
          {checked ? <Check className="size-2.5" strokeWidth={3} /> : null}
        </span>
        {/* ONE LINE PER OPTION, reason included: a bare label hid what the
            choice meant behind a hover. */}
        <span className="min-w-0 text-[13px] leading-snug">
          <span className="font-medium text-foreground">{label}</span>
          {description ? (
            <span className="text-muted-foreground">
              {' - '}
              <span>{description}</span>
            </span>
          ) : null}
        </span>
      </button>
      {/* Revealed only once selected, as the tool description promises the model. */}
      {checked && preview ? (
        <div className="mt-1 ml-5 overflow-x-auto rounded-md border border-border bg-muted/50 px-2.5 py-1.5 font-mono text-[11px]">
          <Markdown text={preview} />
        </div>
      ) : null}
    </div>
  )
}

function QuestionBlock({
  question,
  state,
  onChange,
  onEnter,
  submitOnPick = false,
  onPick,
}: {
  question: InteractionQuestion
  state: { values: string[]; freeText: string }
  onChange: (next: { values: string[]; freeText: string }) => void
  /** Picking an option answers the whole ask (one tap). */
  submitOnPick?: boolean
  /** Answer with this value, now. Only called when `submitOnPick`. */
  onPick?: (value: string) => void
  /** Advance/submit from inside the free-text field (⌘↵). */
  onEnter?: () => void
}) {
  const toggle = useCallback(
    (value: string) => {
      if (question.multiSelect) {
        const has = state.values.includes(value)
        onChange({
          ...state,
          values: has
            ? state.values.filter((v) => v !== value)
            : [...state.values, value],
        })
        return
      }
      // Single-select: re-pressing the chosen option clears it, so a misclick
      // is recoverable without submitting the wrong answer.
      onChange({
        ...state,
        values: state.values.includes(value) ? [] : [value],
      })
    },
    [question.multiSelect, state, onChange],
  )

  const otherSelected = state.values.includes(OTHER)

  // ONE TAP, when there is one tap to make: a single question, single-select,
  // and an option that is not "Other" (which is a request to type). Anything
  // else still goes through the Answer button, because there is more to say.
  //
  // `onPick` is handed the VALUE rather than reading state: the answer is sent
  // in the same tick as the tap, and `state` here still holds the old values.
  const oneTap = useCallback(
    (value: string) => {
      if (submitOnPick && value !== OTHER && !state.values.includes(value)) {
        onPick?.(value)
        return
      }
      toggle(value)
    },
    [toggle, submitOnPick, onPick, state.values],
  )

  const focusOnAppear = useCallback((el: HTMLTextAreaElement | null) => {
    el?.focus()
  }, [])

  return (
    <div className="flex min-h-0 flex-col gap-1.5">
      <div className="flex shrink-0 items-center gap-1.5">
        <span className="rounded-full bg-foreground/[0.06] px-1.5 py-px font-medium text-[10px] text-muted-foreground">
          {question.shortLabel}
        </span>
        {question.multiSelect ? (
          <span className="text-[10px] text-muted-foreground">choose any</span>
        ) : null}
      </div>
      <p className="shrink-0 font-medium text-[13px] text-foreground leading-snug">
        {question.prompt}
      </p>
      {/* The one part that can grow without bound: a long option list scrolls
          here rather than pushing the transcript and the buttons off-screen. */}
      <div className="flex min-h-0 flex-col gap-1 overflow-y-auto">
        {question.options.map((option) => (
          <OptionCard
            key={option.value}
            label={option.label}
            {...(option.description ? { description: option.description } : {})}
            {...(option.preview ? { preview: option.preview } : {})}
            checked={state.values.includes(option.value)}
            multi={question.multiSelect}
            onToggle={() => oneTap(option.value)}
          />
        ))}
        {question.allowFreeText ? (
          <OptionCard
            label="Other"
            description="Answer in your own words."
            checked={otherSelected}
            multi={question.multiSelect}
            onToggle={() => toggle(OTHER)}
          />
        ) : null}
      </div>
      {otherSelected ? (
        <textarea
          value={state.freeText}
          onChange={(e) => onChange({ ...state, freeText: e.target.value })}
          rows={2}
          onKeyDown={(e) => {
            // The textarea is the one place Enter must stay a newline-or-submit
            // decision of its own; ⌘/Ctrl+Enter is the "I am done typing" key.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              onEnter?.()
            }
          }}
          // Focused once, on appear: choosing "Other" is a statement of intent
          // to type, so the caret should already be here. `useCallback` keeps
          // the ref identity stable — an inline arrow is a new function every
          // render, which makes React re-run it on each keystroke and re-focus
          // a field the user is already typing in.
          ref={focusOnAppear}
          placeholder="Your answer"
          className="w-full shrink-0 resize-none rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-blue-500"
        />
      ) : null}
    </div>
  )
}

export function QuestionForm({
  body,
  onSubmit,
  onDismiss,
}: {
  body: QuestionBody
  onSubmit: (answers: Record<string, InteractionAnswerValue>) => Promise<void>
  onDismiss: () => Promise<void>
}) {
  const [selection, setSelection] = useState<Selection>(() =>
    initialSelection(body),
  )
  const [submitting, setSubmitting] = useState(false)
  const [index, setIndex] = useState(0)
  const complete = useMemo(() => isComplete(body, selection), [body, selection])

  const total = body.questions.length
  const current = body.questions[Math.min(index, total - 1)]
  const last = index >= total - 1
  const answered = isAnswered(current ? selection[current.id] : undefined)

  const submit = useCallback(
    async (override?: Selection) => {
      const sending = override ?? selection
      if ((!override && !complete) || submitting) return
      setSubmitting(true)
      try {
        await onSubmit(toAnswers(body, sending))
      } finally {
        // The form unmounts when the answer lands on the log; if the write failed
        // it is still here, and must be usable again rather than stuck spinning.
        setSubmitting(false)
      }
    },
    [body, complete, onSubmit, selection, submitting],
  )

  // One button, two jobs: page forward while questions remain, submit on the
  // last one. Nothing is written until every question has an answer, so the
  // agent still receives the whole ask at once.
  const advance = useCallback(() => {
    if (!answered) return
    if (last) {
      void submit()
      return
    }
    setIndex((i) => Math.min(i + 1, total - 1))
  }, [answered, last, submit, total])

  const dismiss = useCallback(async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      await onDismiss()
    } finally {
      setSubmitting(false)
    }
  }, [onDismiss, submitting])

  if (!current) return null

  return (
    // Capped, not sized: the form takes what one question needs and no more,
    // and never more than a quarter of the column, so the transcript stays
    // readable behind it.
    <div className="flex max-h-[40vh] min-h-0 shrink-0 flex-col border-border border-t bg-background px-4 py-2">
      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-col gap-2">
        <div className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <StatusDot status="asking" />
          <span>the agent is waiting on you</span>
          {total > 1 ? (
            <span className="ml-auto tabular-nums">
              {index + 1} of {total}
            </span>
          ) : null}
        </div>

        <QuestionBlock
          // Keyed by question so the free-text field remounts (and refocuses)
          // per page instead of carrying the previous question's caret.
          key={current.id}
          question={current}
          state={selection[current.id] ?? { values: [], freeText: '' }}
          onChange={(next) =>
            setSelection((prev) => ({ ...prev, [current.id]: next }))
          }
          onEnter={advance}
          submitOnPick={total === 1 && !current.multiSelect}
          onPick={(value) => {
            const next: Selection = {
              ...selection,
              [current.id]: { values: [value], freeText: '' },
            }
            setSelection(next)
            void submit(next)
          }}
        />

        <div className="flex shrink-0 items-center gap-2">
          {index > 0 ? (
            <button
              type="button"
              onClick={() => setIndex((i) => Math.max(i - 1, 0))}
              disabled={submitting}
              className="-ml-1 flex items-center gap-0.5 rounded-full py-1 pr-2 pl-1 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <ChevronLeft className="size-3" />
              <span>Back</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => void dismiss()}
            disabled={submitting}
            className="ml-auto rounded-full px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={advance}
            disabled={!answered || submitting || (last && !complete)}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 font-medium text-[11px] transition-all active:scale-95 ${
              answered && !submitting
                ? 'bg-blue-500 text-white'
                : 'bg-foreground/[0.08] text-muted-foreground'
            }`}
          >
            {submitting ? (
              <span className="size-3 animate-spin rounded-full border-2 border-current/40 border-t-current" />
            ) : null}
            <span>{last ? 'Answer' : 'Next'}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
